import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Box,
  Stack,
  Group,
  Text,
  Badge,
  Tooltip,
  ActionIcon,
  Button,
  Code,
  Divider,
  Tabs,
  Alert,
  Slider,
  Modal,
} from '@mantine/core';
import {
  IconEye,
  IconDownload,
  IconFileCode,
  IconPhoto,
  IconMusic,
  IconFile,
  IconRefresh,
  IconDeviceFloppy,
  IconInfoCircle,
  IconExternalLink,
  IconMaximize,
  IconZoomIn,
  IconZoomOut,
  IconPencil,
  IconCopy,
  IconCheck,
} from '@tabler/icons-react';
import {
  MonacoCodeEditor,
  isTextFileExtension,
  isHtmlFile,
  isSvgFile,
  isImageFile,
  isAudioFile,
} from '@/Components/MonacoCodeEditor';
import { matchesAllowedExtensions } from './explorerUtils';
import { getExplorerFileIcon } from './FolderContents';
import { useFileExplorerContext } from './FileExplorerContext';

interface PanZoomImageViewerProps {
  src: string;
  alt: string;
  maxScale?: number;
  minScale?: number;
  initialScale?: number;
  onOpenNewTab?: () => void;
  onMaximize?: () => void;
  onRefresh?: () => void;
}

export function PanZoomImageViewer({
  src,
  alt,
  maxScale = 10,
  minScale = 0.1,
  initialScale = 1,
  onOpenNewTab,
  onMaximize,
  onRefresh,
}: PanZoomImageViewerProps) {
  const [{ scale, position }, setTransform] = useState<{
    scale: number;
    position: { x: number; y: number };
  }>({
    scale: initialScale,
    position: { x: 0, y: 0 },
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTransform({ scale: initialScale, position: { x: 0, y: 0 } });
  }, [src, initialScale]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.15 : 0.85;
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left - rect.width / 2;
      const mouseY = e.clientY - rect.top - rect.height / 2;

      setTransform((prev) => {
        const nextScale = Math.max(
          minScale,
          Math.min(maxScale, Number((prev.scale * zoomFactor).toFixed(2)))
        );
        return {
          scale: nextScale,
          position: {
            x: mouseX - (mouseX - prev.position.x) * (nextScale / prev.scale),
            y: mouseY - (mouseY - prev.position.y) * (nextScale / prev.scale),
          },
        };
      });
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [minScale, maxScale]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setTransform((prev) => ({
      ...prev,
      position: {
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      },
    }));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isDragging) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {}
      setIsDragging(false);
    }
  };

  const handleReset = () => {
    setTransform({ scale: 1, position: { x: 0, y: 0 } });
  };

  const percent = Math.round(scale * 100);

  return (
    <Stack gap="xs" style={{ flex: 1, height: '100%', minHeight: 0 }}>
      {/* Canvas Viewport */}
      <Box
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleReset}
        style={{
          flex: 1,
          minHeight: '220px',
          borderRadius: '6px',
          border: '1px solid var(--mantine-color-gray-3)',
          background:
            'repeating-conic-gradient(#f8f9fa 0% 25%, #ffffff 0% 50%) 50% / 20px 20px',
          overflow: 'hidden',
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.08s ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            style={{
              maxHeight: '90%',
              maxWidth: '90%',
              objectFit: 'contain',
              boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
              borderRadius: '2px',
            }}
          />
        </div>

        <Box
          style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(4px)',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            color: 'var(--mantine-color-gray-7)',
            pointerEvents: 'none',
            border: '1px solid var(--mantine-color-gray-3)',
          }}
        >
          滾輪縮放 / 拖曳平移 / 雙擊重設
        </Box>
      </Box>

      {/* Zoom Toolbar */}
      <Group justify="space-between" align="center" wrap="wrap" gap="xs">
        <Group gap={6} align="center">
          <Text size="xs" c="dimmed">
            縮放:
          </Text>
          <Tooltip label="縮小">
            <ActionIcon
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() =>
                setTransform((prev) => ({
                  ...prev,
                  scale: Math.max(minScale, Number((prev.scale - 0.25).toFixed(2))),
                }))
              }
              disabled={scale <= minScale}
            >
              <IconZoomOut size={13} />
            </ActionIcon>
          </Tooltip>
          <Slider
            size="xs"
            w={90}
            min={Math.round(minScale * 100)}
            max={Math.round(maxScale * 100)}
            step={10}
            value={percent}
            onChange={(val) => setTransform((prev) => ({ ...prev, scale: val / 100 }))}
            label={(val) => `${val}%`}
          />
          <Tooltip label="放大">
            <ActionIcon
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() =>
                setTransform((prev) => ({
                  ...prev,
                  scale: Math.min(maxScale, Number((prev.scale + 0.25).toFixed(2))),
                }))
              }
              disabled={scale >= maxScale}
            >
              <IconZoomIn size={13} />
            </ActionIcon>
          </Tooltip>
          <Badge size="xs" variant="light" color="gray" w={52} style={{ textAlign: 'center' }}>
            {percent}%
          </Badge>
        </Group>

        <Group gap={4}>
          <Button
            size="compact-xs"
            variant={percent === 100 ? 'light' : 'subtle'}
            color="indigo"
            onClick={handleReset}
          >
            🎯 居中
          </Button>
          <Button
            size="compact-xs"
            variant={percent === 200 ? 'light' : 'subtle'}
            color="gray"
            onClick={() => setTransform((prev) => ({ ...prev, scale: 2 }))}
          >
            200%
          </Button>

          {onOpenNewTab && (
            <Tooltip label="在新分頁開啟">
              <ActionIcon size="sm" variant="light" color="blue" onClick={onOpenNewTab}>
                <IconExternalLink size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {onMaximize && (
            <Tooltip label="全螢幕放大檢視">
              <ActionIcon size="sm" variant="light" color="indigo" onClick={onMaximize}>
                <IconMaximize size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {onRefresh && (
            <Tooltip label="重新整理預覽">
              <ActionIcon size="sm" variant="light" color="gray" onClick={onRefresh}>
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
    </Stack>
  );
}

export function FilePreview() {
  const {
    mode,
    allowedExtensions,
    adapter,
    selectedFile,
    setSelectedFile,
    fileTextContent,
    isLoadingFileContent,
    isContentModified,
    isSavingTextFile,
    onContentChange,
    onSaveTextFile,
    openActionModal,
    onConfirmPick,
  } = useFileExplorerContext();

  const resolveFileUrl = (path: string) => adapter.getFileUrl(path);

  const [htmlPreviewTab, setHtmlPreviewTab] = useState<'code' | 'preview'>('code');
  const [svgPreviewTab, setSvgPreviewTab] = useState<'preview' | 'code'>('preview');
  const [iframeRefreshKey, setIframeRefreshKey] = useState(0);
  const [htmlZoomPercent, setHtmlZoomPercent] = useState(100);
  const [htmlModalOpened, setHtmlModalOpened] = useState(false);
  const [imageModalOpened, setImageModalOpened] = useState(false);

  const htmlContainerRef = useRef<HTMLDivElement>(null);
  const [htmlContainerSize, setHtmlContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!htmlContainerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          setHtmlContainerSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }
    });
    observer.observe(htmlContainerRef.current);
    return () => observer.disconnect();
  }, [htmlPreviewTab, selectedFile]);

  useEffect(() => {
    if (!selectedFile) return;
    if (isHtmlFile(selectedFile)) {
      setHtmlPreviewTab('code');
      setHtmlZoomPercent(100);
    } else if (isSvgFile(selectedFile)) {
      setSvgPreviewTab('preview');
    }
  }, [selectedFile]);

  if (!selectedFile) return null;

  const isAllowed = matchesAllowedExtensions(selectedFile, allowedExtensions);
  const baseHtmlWidth = htmlContainerSize.width > 0 ? htmlContainerSize.width : 600;
  const baseHtmlHeight = htmlContainerSize.height > 0 ? htmlContainerSize.height : 340;
  const htmlScaleFactor = htmlZoomPercent / 100;
  const scaledHtmlWidth = baseHtmlWidth * htmlScaleFactor;
  const scaledHtmlHeight = baseHtmlHeight * htmlScaleFactor;

  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--mantine-color-gray-0)',
      }}
    >
      <Stack gap="xs" style={{ height: '100%', minHeight: 0 }}>
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="xs">
            {getExplorerFileIcon(selectedFile)}
            <Text size="xs" fw={600} style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {selectedFile.split('/').pop()}
            </Text>
            {isContentModified && (
              <Badge color="orange" size="xs" variant="light">
                未儲存
              </Badge>
            )}
            {mode === 'pick' && !isAllowed && (
              <Badge color="red" size="xs" variant="light">
                不符合挑選格式
              </Badge>
            )}
          </Group>

          <Group gap={4}>
            {mode === 'pick' ? (
              <Button
                size="xs"
                color="blue"
                leftSection={<IconCheck size={14} />}
                disabled={!isAllowed}
                onClick={() => onConfirmPick(selectedFile)}
              >
                確認選取
              </Button>
            ) : (
              <>
                <Tooltip label="重新命名檔案">
                  <ActionIcon
                    size="xs"
                    variant="light"
                    color="orange"
                    onClick={() =>
                      openActionModal({ type: 'rename', itemType: 'file', currentPath: selectedFile })
                    }
                  >
                    <IconPencil size={13} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="建立檔案副本">
                  <ActionIcon
                    size="xs"
                    variant="light"
                    color="cyan"
                    onClick={() =>
                      openActionModal({ type: 'duplicate', itemType: 'file', currentPath: selectedFile })
                    }
                  >
                    <IconCopy size={13} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}

            <Tooltip label="下載檔案">
              <ActionIcon
                size="xs"
                variant="light"
                color="blue"
                component="a"
                href={resolveFileUrl(selectedFile)}
                target="_blank"
                download={selectedFile.split('/').pop()}
              >
                <IconDownload size={13} />
              </ActionIcon>
            </Tooltip>
            <Button size="compact-xs" variant="subtle" onClick={() => setSelectedFile(null)}>
              關閉預覽
            </Button>
          </Group>
        </Group>

        <Text size="xs" c="dimmed">
          完整路徑: <Code>{selectedFile}</Code>
        </Text>

        <Divider />

        {/* Image Preview (Non-SVG) with PanZoom engine */}
        {isImageFile(selectedFile) && !isSvgFile(selectedFile) && (
          <PanZoomImageViewer
            src={`${resolveFileUrl(selectedFile)}?_t=${iframeRefreshKey}`}
            alt={selectedFile}
            maxScale={8}
            onOpenNewTab={() => window.open(resolveFileUrl(selectedFile), '_blank')}
            onMaximize={() => setImageModalOpened(true)}
            onRefresh={() => setIframeRefreshKey((k) => k + 1)}
          />
        )}

        {/* Audio Preview */}
        {isAudioFile(selectedFile) && (
          <Box
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <IconMusic size={48} color="var(--mantine-color-violet-6)" />
            <audio controls src={resolveFileUrl(selectedFile)} style={{ width: '100%' }} />
          </Box>
        )}

        {/* HTML Preview & Editor */}
        {isHtmlFile(selectedFile) && (
          <Tabs
            value={htmlPreviewTab}
            onChange={(v) => setHtmlPreviewTab(v as 'code' | 'preview')}
            keepMounted={true}
            keepMountedMode="display-none"
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <Tabs.List mb="xs">
              <Tabs.Tab value="code" leftSection={<IconFileCode size={14} />}>
                程式碼檢視
              </Tabs.Tab>
              <Tabs.Tab value="preview" leftSection={<IconEye size={14} />}>
                即時預覽 (Iframe)
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="code" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box style={{ flex: 1, minHeight: '240px' }}>
                <MonacoCodeEditor
                  value={fileTextContent}
                  onChange={(val) => onContentChange(val)}
                  fileName={selectedFile}
                  onSave={mode === 'manage' ? onSaveTextFile : undefined}
                  loading={isLoadingFileContent}
                  height="100%"
                  readOnly={mode === 'pick'}
                />
              </Box>
              {mode === 'manage' && (
                <Group justify="space-between" align="center" mt="xs">
                  <Text size="xs" c="dimmed">
                    按 <Code>Ctrl+S</Code> / <Code>Cmd+S</Code> 儲存
                  </Text>
                  <Button
                    size="xs"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={onSaveTextFile}
                    loading={isSavingTextFile}
                    disabled={!isContentModified}
                  >
                    儲存修改
                  </Button>
                </Group>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="preview" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box
                ref={htmlContainerRef}
                style={{
                  flex: 1,
                  minHeight: '240px',
                  position: 'relative',
                  borderRadius: '6px',
                  overflow: 'auto',
                  border: '1px solid var(--mantine-color-gray-3)',
                  background:
                    'repeating-conic-gradient(#f8f9fa 0% 25%, #ffffff 0% 50%) 50% / 20px 20px',
                  display: 'flex',
                }}
              >
                <Box
                  style={{
                    margin: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '100%',
                    minHeight: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    style={{
                      width: `${scaledHtmlWidth}px`,
                      height: `${scaledHtmlHeight}px`,
                      position: 'relative',
                      flexShrink: 0,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#ffffff',
                    }}
                  >
                    <Box
                      style={{
                        width: `${baseHtmlWidth}px`,
                        height: `${baseHtmlHeight}px`,
                        transform: `scale(${htmlScaleFactor})`,
                        transformOrigin: 'top left',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}
                    >
                      <iframe
                        key={iframeRefreshKey}
                        src={`${resolveFileUrl(selectedFile)}?_t=${iframeRefreshKey}`}
                        title={selectedFile}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          backgroundColor: '#ffffff',
                        }}
                        sandbox="allow-scripts allow-forms"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Group justify="space-between" align="center" mt="xs" wrap="wrap" gap="xs">
                <Group gap={6} align="center">
                  <Text size="xs" c="dimmed">
                    縮放:
                  </Text>
                  <Tooltip label="縮小 10%">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => setHtmlZoomPercent((p) => Math.max(25, p - 10))}
                      disabled={htmlZoomPercent <= 25}
                    >
                      <IconZoomOut size={13} />
                    </ActionIcon>
                  </Tooltip>
                  <Slider
                    size="xs"
                    w={90}
                    min={25}
                    max={200}
                    step={5}
                    value={htmlZoomPercent}
                    onChange={setHtmlZoomPercent}
                    label={(val) => `${val}%`}
                  />
                  <Tooltip label="放大 10%">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => setHtmlZoomPercent((p) => Math.min(200, p + 10))}
                      disabled={htmlZoomPercent >= 200}
                    >
                      <IconZoomIn size={13} />
                    </ActionIcon>
                  </Tooltip>
                  <Badge size="xs" variant="light" color="gray">
                    {htmlZoomPercent}%
                  </Badge>
                  <Button
                    size="compact-xs"
                    variant={htmlZoomPercent === 100 ? 'light' : 'subtle'}
                    color="gray"
                    onClick={() => setHtmlZoomPercent(100)}
                  >
                    100%
                  </Button>
                </Group>

                <Group gap={4}>
                  <Tooltip label="在新分頁開啟完整預覽">
                    <ActionIcon
                      size="sm"
                      variant="light"
                      color="blue"
                      component="a"
                      href={resolveFileUrl(selectedFile)}
                      target="_blank"
                    >
                      <IconExternalLink size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="全螢幕放大檢視">
                    <ActionIcon
                      size="sm"
                      variant="light"
                      color="indigo"
                      onClick={() => setHtmlModalOpened(true)}
                    >
                      <IconMaximize size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="重新整理預覽">
                    <ActionIcon
                      size="sm"
                      variant="light"
                      color="gray"
                      onClick={() => setIframeRefreshKey((k) => k + 1)}
                    >
                      <IconRefresh size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Tabs.Panel>
          </Tabs>
        )}

        {/* SVG Preview & Editor with PanZoom engine */}
        {isSvgFile(selectedFile) && (
          <Tabs
            value={svgPreviewTab}
            onChange={(v) => setSvgPreviewTab(v as 'preview' | 'code')}
            keepMounted={true}
            keepMountedMode="display-none"
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <Tabs.List mb="xs">
              <Tabs.Tab value="preview" leftSection={<IconPhoto size={14} />}>
                圖形預覽 (Pan & Zoom)
              </Tabs.Tab>
              <Tabs.Tab value="code" leftSection={<IconFileCode size={14} />}>
                原始碼檢視
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="preview" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <PanZoomImageViewer
                src={`${resolveFileUrl(selectedFile)}?_t=${iframeRefreshKey}`}
                alt={selectedFile}
                maxScale={10}
                onOpenNewTab={() => window.open(resolveFileUrl(selectedFile), '_blank')}
                onMaximize={() => setImageModalOpened(true)}
                onRefresh={() => setIframeRefreshKey((k) => k + 1)}
              />
            </Tabs.Panel>

            <Tabs.Panel value="code" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box style={{ flex: 1, minHeight: '240px' }}>
                <MonacoCodeEditor
                  value={fileTextContent}
                  onChange={(val) => onContentChange(val)}
                  fileName={selectedFile}
                  onSave={mode === 'manage' ? onSaveTextFile : undefined}
                  loading={isLoadingFileContent}
                  height="100%"
                  readOnly={mode === 'pick'}
                />
              </Box>
              {mode === 'manage' && (
                <Group justify="space-between" align="center" mt="xs">
                  <Text size="xs" c="dimmed">
                    按 <Code>Ctrl+S</Code> / <Code>Cmd+S</Code> 儲存
                  </Text>
                  <Button
                    size="xs"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={onSaveTextFile}
                    loading={isSavingTextFile}
                    disabled={!isContentModified}
                  >
                    儲存修改
                  </Button>
                </Group>
              )}
            </Tabs.Panel>
          </Tabs>
        )}

        {/* Non-HTML, Non-SVG Text / JSON / Code Editor */}
        {isTextFileExtension(selectedFile) && !isHtmlFile(selectedFile) && !isSvgFile(selectedFile) && (
          <Stack gap="xs" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {(selectedFile.toLowerCase().endsWith('extension.json') ||
              selectedFile.toLowerCase().endsWith('manifest.json')) && (
              <Alert icon={<IconInfoCircle size={16} />} color="teal" p="xs">
                <Text size="xs">此為主要描述檔 (Manifest)</Text>
              </Alert>
            )}

            <Box style={{ flex: 1, minHeight: '240px' }}>
              <MonacoCodeEditor
                value={fileTextContent}
                onChange={(val) => onContentChange(val)}
                fileName={selectedFile}
                onSave={mode === 'manage' ? onSaveTextFile : undefined}
                loading={isLoadingFileContent}
                height="100%"
                readOnly={mode === 'pick'}
              />
            </Box>

            {mode === 'manage' && (
              <Group justify="space-between" align="center">
                <Text size="xs" c="dimmed">
                  按 <Code>Ctrl+S</Code> / <Code>Cmd+S</Code> 儲存
                </Text>
                <Button
                  size="xs"
                  leftSection={<IconDeviceFloppy size={14} />}
                  onClick={onSaveTextFile}
                  loading={isSavingTextFile}
                  disabled={!isContentModified}
                >
                  儲存修改
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {/* Other Binary Files */}
        {!isImageFile(selectedFile) && !isAudioFile(selectedFile) && !isTextFileExtension(selectedFile) && (
          <Box
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <IconFile size={48} color="gray" />
            <Text size="xs" c="dimmed">
              二進位檔案，不支援即時文字預覽
            </Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDownload size={14} />}
              component="a"
              href={resolveFileUrl(selectedFile)}
              target="_blank"
              download={selectedFile.split('/').pop()}
            >
              下載檔案檢視
            </Button>
          </Box>
        )}
      </Stack>

      {/* HTML Maximize Modal */}
      {htmlModalOpened && selectedFile && (
        <Modal
          opened={htmlModalOpened}
          onClose={() => setHtmlModalOpened(false)}
          title={
            <Group gap="xs">
              <IconFileCode size={18} color="var(--mantine-color-blue-6)" />
              <Text fw={600}>{selectedFile}</Text>
            </Group>
          }
          size="90%"
          centered
        >
          <Box
            style={{
              width: '100%',
              height: '75vh',
              borderRadius: '6px',
              overflow: 'hidden',
              border: '1px solid var(--mantine-color-gray-3)',
            }}
          >
            <iframe
              src={`${resolveFileUrl(selectedFile)}?_t=${iframeRefreshKey}`}
              title={selectedFile}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#ffffff',
              }}
              sandbox="allow-scripts allow-forms"
            />
          </Box>
        </Modal>
      )}

      {/* Image / SVG Maximize Modal */}
      {imageModalOpened && selectedFile && (
        <Modal
          opened={imageModalOpened}
          onClose={() => setImageModalOpened(false)}
          title={
            <Group gap="xs">
              <IconPhoto size={18} color="var(--mantine-color-grape-6)" />
              <Text fw={600}>{selectedFile}</Text>
            </Group>
          }
          size="90%"
          centered
        >
          <Box style={{ width: '100%', height: '75vh' }}>
            <PanZoomImageViewer
              src={`${resolveFileUrl(selectedFile)}?_t=${iframeRefreshKey}`}
              alt={selectedFile}
              maxScale={10}
            />
          </Box>
        </Modal>
      )}
    </Paper>
  );
}
